import { type ReactNode, useId, useState } from 'react';

import { RiArrowRightSLine } from '@remixicon/react';
import classNames from 'classnames';

import { Button, Text } from '@backstage/ui';

import { ConversationsV2ToolConfirmationResponseDecision } from '@spotify/confidence-graphql';

import type { ToolConfirmationRenderProps } from '../../extensions';
import { ResourceDisplayNameProvider } from '../ToolConfirmationMessage/ResourceDisplayNameContext';
import {
  isPlainObject,
  resolveInputBody,
} from '../ToolConfirmationMessage/inputUtils';
import styles from './ResourceConfirmationCard.module.css';

export const CONFIRMATION_ACTIONS = [
  {
    decision: ConversationsV2ToolConfirmationResponseDecision.Allow,
    label: 'Approve',
    variant: 'primary' as const,
  },
  {
    decision: ConversationsV2ToolConfirmationResponseDecision.AllowAll,
    label: 'Approve all',
    variant: 'tertiary' as const,
  },
  {
    decision: ConversationsV2ToolConfirmationResponseDecision.Deny,
    label: 'Cancel',
    variant: 'tertiary' as const,
  },
];

interface ResourceConfirmationCardProps extends ToolConfirmationRenderProps {
  icon?: ReactNode;
  title: string;
  children: ReactNode;
  resourceHints?: string[];
}

export const ResourceConfirmationCard = ({
  icon,
  title,
  children,
  input,
  resourceHints,
  actionsDisabled,
  pendingDecision,
  error,
  onDecide,
}: ResourceConfirmationCardProps) => {
  const [rawExpanded, setRawExpanded] = useState(false);
  const rawContentId = useId();
  const parsedInput = isPlainObject(input) ? resolveInputBody(input) : input;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        <Text variant="body-medium" weight="bold">
          {title}
        </Text>
      </div>
      <div className={styles.body}>
        <div className={styles.resourceContainer}>
          <ResourceDisplayNameProvider
            input={parsedInput}
            additionalNames={resourceHints}
          >
            {children}
          </ResourceDisplayNameProvider>
        </div>
        <Button
          variant="tertiary"
          size="small"
          className={styles.rawPayloadToggle}
          aria-expanded={rawExpanded}
          aria-controls={rawContentId}
          onPress={() => setRawExpanded(v => !v)}
        >
          View raw payload
          <RiArrowRightSLine
            size={12}
            className={classNames(styles.chevron, {
              [styles.chevronExpanded]: rawExpanded,
            })}
          />
        </Button>
        {rawExpanded && (
          <pre id={rawContentId} className={styles.rawPayloadContent}>
            {JSON.stringify(input, null, 2)}
          </pre>
        )}
      </div>
      <hr className={styles.divider} />
      <div className={styles.actions}>
        <div className={styles.buttons}>
          {CONFIRMATION_ACTIONS.map(({ decision, label, variant }) => (
            <Button
              key={label}
              variant={variant}
              size="small"
              isDisabled={actionsDisabled}
              isPending={pendingDecision === decision}
              onPress={() => onDecide(decision)}
            >
              {label}
            </Button>
          ))}
        </div>
        {error && (
          <Text variant="body-small" color="danger">
            {error.message}
          </Text>
        )}
      </div>
    </div>
  );
};
